import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { useWindmillFlows } from '../../hooks/useWindmillFlows';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { IWindmillFlow } from '../../schemas/apis/interface';
import { Eye } from '@phosphor-icons/react';
import { HeaderContainer } from '../../components/dashboard/AgentInfoHeader';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../constants/routes';
import theme from '../../theme';
import React from 'react';
import NoDataFallback from '../../components/common/NoDataFallback';

const WindmillFlows = () => {
  const { flows, isLoading, error, isError } = useWindmillFlows();
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const handleViewFlow = (flow: IWindmillFlow) => {
    console.log(flow);
    const sanitizedPath = flow.path.replace(/\//g, '-');
    navigate(routes.quibbleFlow.replace(':flowPath', sanitizedPath));
  };

  const columns: GridColDef[] = [
    {
      field: 'workspace_id',
      headerName: 'Workspace ID',
      headerClassName: 'data-grid-header',
      width: 150,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        return params.value && params.value.toString().trim() !== ''
          ? params.value
          : '-';
      },
    },
    {
      field: 'summary',
      headerName: 'Flow Name',
      headerClassName: 'data-grid-header',
      width: 300,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        return params.value && params.value.toString().trim() !== ''
          ? params.value
          : '-';
      },
    },
    {
      field: 'path',
      headerName: 'Path',
      headerClassName: 'data-grid-header',
      minWidth: 250,
      flex: 1,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        // Check if value exists and is not empty
        if (!params.value || params.value.toString().trim() === '') {
          return '-';
        }

        return (
          <Box
            component="button"
            sx={{
              color: 'custom.textBlue',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <Tooltip title="View Flow Details">
              <Typography
                sx={{
                  overflow: 'hidden',
                  color: 'custom.textBlue',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: '"DM Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  letterSpacing: '-0.14px',
                  width: '100%',
                  maxWidth: '220px',
                }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      headerClassName: 'data-grid-header',
      width: 300,
      filterable: true,
      renderCell: params => {
        // Check if value exists and is not empty
        if (!params.value || params.value.toString().trim() === '') {
          return '-';
        }

        return (
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
            title={params.value}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'edited_at',
      headerName: 'Edited At',
      headerClassName: 'data-grid-header',
      width: 180,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '-';
        }
        try {
          return format(new Date(params.value), 'MMM dd, yyyy HH:mm');
        } catch (e: any) {
          console.log(e);
          return '-';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerClassName: 'data-grid-header',
      align: 'center',
      headerAlign: 'center',
      width: 120,
      sortable: false,
      renderCell: params => (
        <Tooltip title="View Flow">
          <IconButton onClick={() => handleViewFlow(params.row)} size="small">
            <Eye />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const skeletonRows = Array.from({ length: 100 }, (_, index) => ({
    id: index,
    workspace_id: index,
    summary: index,
    path: index,
    description: index,
    edited_by: index,
    edited_at: index,
    actions: index,
  }));

  // Define skeleton columns that match the actual table
  const skeletonColumns: GridColDef[] = [
    {
      field: 'workspace_id',
      headerName: 'Workspace ID',
      headerClassName: 'data-grid-header',
      width: 150,
      renderCell: () => <Skeleton variant="text" width={100} />,
    },
    {
      field: 'summary',
      headerName: 'Flow Name',
      headerClassName: 'data-grid-header',
      width: 300,
      renderCell: () => <Skeleton variant="text" width={200} />,
    },
    {
      field: 'path',
      headerName: 'Path',
      headerClassName: 'data-grid-header',
      minWidth: 250,
      flex: 1,
      renderCell: () => <Skeleton variant="text" width={180} />,
    },
    {
      field: 'description',
      headerName: 'Description',
      headerClassName: 'data-grid-header',
      width: 300,
      renderCell: () => <Skeleton variant="text" width={220} />,
    },
    {
      field: 'edited_by',
      headerName: 'Edited By',
      headerClassName: 'data-grid-header',
      width: 150,
      renderCell: () => <Skeleton variant="text" width={100} />,
    },
    {
      field: 'edited_at',
      headerName: 'Edited At',
      headerClassName: 'data-grid-header',
      width: 180,
      renderCell: () => <Skeleton variant="text" width={130} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerClassName: 'data-grid-header',
      align: 'center',
      headerAlign: 'center',
      width: 120,
      renderCell: () => <Skeleton variant="circular" width={22} height={22} />,
    },
  ];

  if (isLoading) {
    return (
      <LayoutWithSidebar margin={2}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <HeaderContainer
            sx={{
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: '12px',
            }}
          >
            <Typography
              variant="h6"
              color="custom.textBlue"
              sx={{ fontSize: '24px', fontWeight: 600 }}
            >
              Workflows
            </Typography>
          </HeaderContainer>

          <Box
            sx={{
              flexGrow: 1,
              height: 'calc(100vh - 120px)',
              minHeight: 400,
              width: '100%',
              bgcolor: '#fff',
            }}
          >
            <DataGrid
              rows={skeletonRows}
              columns={skeletonColumns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100]}
              disableRowSelectionOnClick
              disableColumnFilter={false}
              disableColumnSelector={false}
              disableDensitySelector={false}
              sx={{
                border: `1px solid ${theme.palette.lightGray.main}`,
                borderRadius: '12px',
                height: '100%',
                flex: 1,
                '& .data-grid-header': {
                  bgcolor: '#F8F9FC',
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                  padding: '12px 16px',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  '&:focus, &:focus-within': {
                    outline: 'none !important',
                  },
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
              }}
            />
          </Box>
        </Box>
      </LayoutWithSidebar>
    );
  }

  if (isError) {
    return (
      <LayoutWithSidebar margin={2}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <HeaderContainer
            sx={{
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: '12px',
            }}
          >
            <Typography
              variant="h6"
              color="custom.textBlue"
              sx={{ fontSize: '24px', fontWeight: 600 }}
            >
              Workflows
            </Typography>
          </HeaderContainer>

          <Alert severity="error">
            Error loading flows: {error?.message || 'Unknown error occurred'}
          </Alert>
        </Box>
      </LayoutWithSidebar>
    );
  }

  if (!flows || flows.length === 0) {
    return (
      <LayoutWithSidebar margin={2}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <HeaderContainer
            sx={{
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: '12px',
            }}
          >
            <Typography
              variant="h6"
              color="custom.textBlue"
              sx={{ fontSize: '24px', fontWeight: 600 }}
            >
              Workflows
            </Typography>
          </HeaderContainer>

          <Box
            sx={{
              flexGrow: 1,
              height: 'calc(100vh - 280px)',
              minHeight: 400,
              width: '100%',
              bgcolor: '#fff',
            }}
          >
            <NoDataFallback
              message="No Workflows available"
              description="There are no Workflows to display at the moment."
            />
          </Box>
        </Box>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar margin={2}>
      <Box
        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <HeaderContainer
          sx={{
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '12px',
          }}
        >
          <Typography
            variant="h6"
            color="custom.textBlue"
            sx={{ fontSize: '24px', fontWeight: 600 }}
          >
            Workflows
          </Typography>
        </HeaderContainer>

        <Box
          sx={{
            flexGrow: 1,
            height: 'calc(100vh - 120px)',
            minHeight: 400,
            width: '100%',
            bgcolor: '#fff',
          }}
        >
          <DataGrid
            rows={flows}
            columns={columns}
            getRowId={row => row.path}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100]}
            disableRowSelectionOnClick
            loading={isLoading}
            disableColumnFilter={false}
            disableColumnSelector={false}
            disableDensitySelector={false}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              border: `1px solid ${theme.palette.lightGray.main}`,
              borderRadius: '12px',
              height: '100%',
              flex: 1,
              '& .data-grid-header': {
                bgcolor: '#F8F9FC',
                color: 'custom.textBlue',
                fontSize: '14px',
                fontWeight: 600,
                padding: '12px 16px',
              },
              '& .MuiDataGrid-cell': {
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                '&:focus, &:focus-within': {
                  outline: 'none !important',
                },
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none !important',
              },
            }}
          />
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default WindmillFlows;
